import { BadRequestError } from "@/wab/shared/ApiErrors/errors";
import type { CmsFieldMeta } from "@/wab/shared/ApiSchema";
import { ALLOWED_UNIQUE_TYPES } from "@/wab/shared/cms";
import { toVarName } from "@/wab/shared/codegen/util";
import {
  canonicalJson,
  CmsMediaProof,
  cmsDraftMediaIds,
  cmsDraftValueMatches,
  cmsFieldHasMedia,
  cmsMediaAssertKnown,
  cmsMediaCollect,
  cmsMediaIdLeaves,
  utf8Bytes,
} from "@/wab/shared/copilot/assistant-cms-draft";
import { assistantJsonSchema } from "@/wab/shared/copilot/assistant-protocol";
import { z } from "zod";

/**
 * Pure model of the native model-schema editor (CmsModelDetails). Every native
 * field operation is a local mutation of the whole `schema.fields` array that
 * Save then PUTs; there is no per-field server call. These typed changes are
 * applied in order to the locked table's CURRENT fields, so attributes the
 * assistant never saw (defaults, legacy keys, nested subtrees) are carried by
 * identity, and the result goes through `DbMgr.updateCmsTable` unchanged.
 */
const DANGEROUS_KEYS = new Set(["__proto__", "constructor", "prototype"]);
const identifierText = z
  .string()
  .min(1)
  .max(500)
  .refine((k) => !DANGEROUS_KEYS.has(k), "Dangerous field identifier");

export const CMS_SCHEMA_CHANGE_BOUNDS = {
  changes: 50,
  /** Metadata reads refuse deeper trees and more fields, so a write must not create them. */
  pathDepth: 8,
  totalFields: 500,
  defaultValueUtf8Bytes: 32_000,
} as const;

export const CMS_FIELD_TYPES = [
  "text",
  "long-text",
  "number",
  "image",
  "file",
  "date-time",
  "boolean",
  "color",
  "rich-text",
  "ref",
  "list",
  "object",
  "enum",
] as const;
export type CmsFieldTypeName = (typeof CMS_FIELD_TYPES)[number];
const fieldType = z.enum(CMS_FIELD_TYPES);

/** Identifier path from the top-level field list into nested list/object fields. */
const fieldPath = z
  .array(identifierText)
  .min(1)
  .max(CMS_SCHEMA_CHANGE_BOUNDS.pathDepth);
const defaultValueByLocale = z
  .record(
    z.string().max(500).refine((k) => !DANGEROUS_KEYS.has(k)),
    assistantJsonSchema,
  )
  .refine(
    (v) =>
      utf8Bytes(canonicalJson(v)) <=
      CMS_SCHEMA_CHANGE_BOUNDS.defaultValueUtf8Bytes,
    "Default values exceed byte bound",
  );
/** Exactly the attributes the native field form edits; at least one. */
export const cmsFieldSetSchema = z
  .object({
    label: z.string().max(1000).nullable(),
    helperText: z.string().max(5000),
    required: z.boolean(),
    hidden: z.boolean(),
    localized: z.boolean(),
    unique: z.boolean(),
    minChars: z.number().int().min(0).nullable(),
    maxChars: z.number().int().min(0).nullable(),
    options: z.array(z.string().max(1000)).max(100),
    tableId: z.string().min(1).max(500),
    /** Whole map per locale (`""` is the default locale); a null value unsets that locale. */
    defaultValueByLocale,
  })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, "Empty field change");
export type CmsFieldSet = z.infer<typeof cmsFieldSetSchema>;

export const cmsSchemaChangeSchema = z.discriminatedUnion("op", [
  z
    .object({
      op: z.literal("add_field"),
      /** Absent: the top-level list. Otherwise the nested list/object field that receives the new field. */
      parentPath: z
        .array(identifierText)
        .min(1)
        .max(CMS_SCHEMA_CHANGE_BOUNDS.pathDepth - 1)
        .optional(),
      identifier: identifierText,
      fieldType,
      index: z.number().int().min(0).max(1000).optional(),
      set: cmsFieldSetSchema.optional(),
    })
    .strict(),
  z
    .object({ op: z.literal("edit_field"), path: fieldPath, set: cmsFieldSetSchema })
    .strict(),
  z
    .object({ op: z.literal("rename_field"), path: fieldPath, newIdentifier: identifierText })
    .strict(),
  z
    .object({ op: z.literal("change_field_type"), path: fieldPath, fieldType })
    .strict(),
  z.object({ op: z.literal("delete_field"), path: fieldPath }).strict(),
  z
    .object({ op: z.literal("move_field"), path: fieldPath, toIndex: z.number().int().min(0).max(1000) })
    .strict(),
]);
export type CmsSchemaChange = z.infer<typeof cmsSchemaChangeSchema>;
export const cmsSchemaChangesSchema = z
  .array(cmsSchemaChangeSchema)
  .min(1)
  .max(CMS_SCHEMA_CHANGE_BOUNDS.changes);

/** What actually happened per change; the receipt carries these next to the persisted readback. */
export const cmsSchemaChangeNoteSchema = z
  .object({
    op: z.enum(["add_field", "edit_field", "rename_field", "change_field_type", "delete_field", "move_field"]),
    path: z.array(z.string().max(500)).max(CMS_SCHEMA_CHANGE_BOUNDS.pathDepth),
    /** Add/rename: the identifier as stored (top-level identifiers are normalized by the native server). */
    identifier: z.string().max(500).optional(),
    /** Type change: type-specific keys the new type no longer carries. */
    discarded: z.array(z.string().max(100)).max(20).optional(),
  })
  .strict();
export type CmsSchemaChangeNote = z.infer<typeof cmsSchemaChangeNoteSchema>;

export type CmsSchemaApplyContext = {
  /** Configured database locales (the default locale `""` is implicit). */
  locales: readonly string[];
  /** Live, unarchived tables of the same database: what the native ref selector offers. */
  refTableIds: ReadonlySet<string>;
  /** Native `DEVFLAGS.cmsUniqueFields`: the unique toggle exists only behind it. */
  allowUnique: boolean;
  /**
   * Upload records the server verified for this actor, project and database
   * (active, valid native metadata), by media id. A default may carry the
   * transport-only `mediaId` on an image/file leaf only if the record is here.
   */
  media: ReadonlyMap<string, { kind: "image" | "file"; metadata: unknown }>;
};
type PendingMedia = { canon: string; owned: Map<string, string> };
/** Per-run provenance state: the untouched fields and the verified media ids of defaults set in this run. */
type MediaState = {
  baseline: WeakMap<Field, Field>;
  pending: WeakMap<Field, Map<string, PendingMedia>>;
};

type Field = Record<string, any>;
const hasOwn = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);
const fail = (message: string): never => {
  throw new BadRequestError(message);
};
const isContainer = (f: Field) => f.type === "list" || f.type === "object";
const TEXT_LIKE = new Set(["text", "long-text"]);
const UNIQUE_TYPES = new Set<string>(ALLOWED_UNIQUE_TYPES as readonly string[]);
/** Native type change copies only these keys of the old field (CmsModelDetails type handler). */
const BASE_KEYS = ["identifier", "name", "helperText", "required", "hidden", "localized", "unique"] as const;
const TYPE_SPECIFIC_KEYS = ["tableId", "options", "minChars", "maxChars", "defaultValue", "fields"] as const;

/** Top-level identifiers are rewritten by the native server (`normalizeTableSchema`); nested ones never are. */
export const cmsFieldStoredIdentifier = (identifier: string, topLevel: boolean) =>
  topLevel ? toVarName(identifier) : identifier;

function countFields(fields: Field[]): number {
  let n = 0;
  for (const f of fields) {
    n += 1;
    if (isContainer(f) && Array.isArray(f.fields)) n += countFields(f.fields);
  }
  return n;
}

function siblingsOf(root: Field[], parentPath: readonly string[]): { siblings: Field[]; parent: Field | undefined } {
  let siblings = root;
  let parent: Field | undefined;
  for (const id of parentPath) {
    const next = siblings.find((f) => f.identifier === id);
    if (!next) return fail(`CMS schema path not found: ${parentPath.join("/")}`);
    if (!isContainer(next) || !Array.isArray(next.fields))
      return fail(`CMS schema path is not a list or object field: ${parentPath.join("/")}`);
    parent = next;
    siblings = next.fields;
  }
  return { siblings, parent };
}
function locate(root: Field[], path: readonly string[]) {
  const { siblings } = siblingsOf(root, path.slice(0, -1));
  const id = path[path.length - 1];
  const index = siblings.findIndex((f) => f.identifier === id);
  if (index < 0) return fail(`CMS schema field not found: ${path.join("/")}`);
  return { siblings, index, field: siblings[index], topLevel: path.length === 1 };
}
function assertUniqueSiblings(siblings: Field[], what: string) {
  const seen = new Set<string>();
  for (const f of siblings) {
    if (seen.has(f.identifier)) fail(`CMS schema would have duplicate field identifiers (${what})`);
    seen.add(f.identifier);
  }
}

/** Native new-field object (CmsModelDetails add handler): a text field with these exact keys. */
function nativeNewField(identifier: string): Field {
  return {
    identifier,
    name: "",
    localized: false,
    helperText: "",
    required: false,
    hidden: false,
    unique: false,
    type: "text",
    defaultValueByLocale: {},
  };
}

/**
 * Native type change keeps the base keys, resets defaults, gives list/object an
 * empty `fields` and clears `unique` outside the unique-capable types. The
 * assistant also keeps the free-text `label`: dropping an authored label is not
 * a documented intent of the native handler. Type-specific keys cannot apply to
 * the new type and are reported as `discarded`.
 */
function retype(field: Field, to: CmsFieldTypeName): { field: Field; discarded: string[] } {
  const next: Field = {};
  for (const k of BASE_KEYS) if (hasOwn(field, k)) next[k] = field[k];
  if (hasOwn(field, "label")) next.label = field.label;
  next.type = to;
  next.defaultValueByLocale = {};
  if (to === "list" || to === "object") next.fields = [];
  if (!UNIQUE_TYPES.has(to)) next.unique = false;
  const discarded: string[] = [];
  for (const k of TYPE_SPECIFIC_KEYS) {
    if (!hasOwn(field, k)) continue;
    if (k === "fields" && (!Array.isArray(field.fields) || field.fields.length === 0)) continue;
    discarded.push(k);
  }
  if (hasOwn(field, "defaultValueByLocale") && Object.keys(field.defaultValueByLocale ?? {}).length > 0)
    discarded.push("defaultValueByLocale");
  return { field: next, discarded };
}

const MEDIA_ID_MESSAGE = "CMS media id is not usable in this project and database";

/** Sorted distinct transport media ids carried by the defaults of the requested changes. */
export function cmsSchemaChangeMediaIds(changes: readonly CmsSchemaChange[]): string[] {
  const ids = new Set<string>();
  for (const change of changes) {
    if (change.op !== "add_field" && change.op !== "edit_field") continue;
    const defaults = change.set?.defaultValueByLocale;
    if (!defaults) continue;
    for (const value of Object.values(defaults))
      for (const id of cmsDraftMediaIds(value).ids) ids.add(id);
  }
  return [...ids].sort();
}

/** Identifier/type skeleton of a field subtree: what decides which positions are media positions. */
const shapeOf = (f: Field): unknown => [
  f.identifier,
  f.type,
  isContainer(f) && Array.isArray(f.fields) ? f.fields.map(shapeOf) : null,
];

/**
 * A new or changed default may keep media only where the same field and locale
 * already held it, or at a position whose `mediaId` names a verified upload
 * with exactly that metadata. A default left unchanged on an unchanged subtree
 * is legacy data and is carried as is.
 */
function assertDefaultMediaProvenance(fields: Field[], state: MediaState) {
  for (const field of fields) {
    const defaults = field.defaultValueByLocale as Record<string, unknown> | undefined;
    const base = state.baseline.get(field);
    if (defaults && Object.keys(defaults).length > 0 && cmsFieldHasMedia(field as CmsFieldMeta)) {
      const sameShape = !!base && canonicalJson(shapeOf(base)) === canonicalJson(shapeOf(field));
      for (const [locale, value] of Object.entries(defaults)) {
        const baseDefault = (base?.defaultValueByLocale as Record<string, unknown> | undefined)?.[locale];
        if (sameShape && baseDefault !== undefined && canonicalJson(baseDefault) === canonicalJson(value)) continue;
        const proof = new CmsMediaProof();
        if (base && baseDefault !== undefined) cmsMediaCollect(base as CmsFieldMeta, baseDefault, proof);
        const pending = state.pending.get(field)?.get(locale);
        const owned = pending && pending.canon === canonicalJson(value) ? pending.owned : undefined;
        cmsMediaAssertKnown(field as CmsFieldMeta, value, proof, owned);
      }
    }
    if (isContainer(field) && Array.isArray(field.fields)) assertDefaultMediaProvenance(field.fields, state);
  }
}

function applySet(field: Field, set: CmsFieldSet, topLevel: boolean, ctx: CmsSchemaApplyContext, state: MediaState) {
  const type = field.type as string;
  if (set.label !== undefined) {
    if (set.label === null) delete field.label;
    else field.label = set.label;
  }
  if (set.helperText !== undefined) field.helperText = set.helperText;
  if (set.required !== undefined) {
    if (isContainer(field)) fail("Native editor has no required toggle for list and object fields");
    field.required = set.required;
  }
  if (set.hidden !== undefined) field.hidden = set.hidden;
  if (set.localized !== undefined) {
    if (!topLevel) fail("Native editor localizes only top-level fields");
    if (set.localized && field.unique) fail("Unique fields cannot be localized");
    field.localized = set.localized;
  }
  if (set.unique !== undefined) {
    if (!topLevel) fail("Native editor sets unique only on top-level fields");
    if (set.unique) {
      if (!ctx.allowUnique) fail("Unique fields are not enabled in this deployment");
      if (!UNIQUE_TYPES.has(type)) fail(`Fields of type ${type} cannot be unique`);
      if (field.localized || set.localized) fail("Unique fields cannot be localized");
    }
    field.unique = set.unique;
  }
  if (set.minChars !== undefined || set.maxChars !== undefined) {
    if (!TEXT_LIKE.has(type)) fail("Min/max characters apply only to text and long-text fields");
    for (const k of ["minChars", "maxChars"] as const) {
      if (set[k] === undefined) continue;
      if (set[k] === null) delete field[k];
      else field[k] = set[k];
    }
    if (field.minChars !== undefined && field.maxChars !== undefined && field.minChars > field.maxChars)
      fail("minChars exceeds maxChars");
  }
  if (set.options !== undefined) {
    if (type !== "enum") fail("Options apply only to enum fields");
    field.options = [...set.options];
  }
  if (set.tableId !== undefined) {
    if (type !== "ref") fail("tableId applies only to ref fields");
    if (!ctx.refTableIds.has(set.tableId)) fail("Ref target is not a live table of this database");
    field.tableId = set.tableId;
  }
  if (set.defaultValueByLocale !== undefined) {
    const next: Record<string, unknown> = {};
    const pending = new Map<string, PendingMedia>();
    const allowed = new Set(["", ...(topLevel ? ctx.locales : [])]);
    for (const [locale, value] of Object.entries(set.defaultValueByLocale)) {
      if (!allowed.has(locale))
        fail(topLevel ? "Default value locale is not configured" : "Nested fields have only a default-locale default");
      if (value === null) continue;
      const withIds = cmsDraftMediaIds(value);
      const stored = withIds.stripped;
      // A default is not a required-checked entry value; the typed shape and bounds still apply.
      let matches = false;
      try {
        matches = cmsDraftValueMatches({ ...field, required: false } as unknown as CmsFieldMeta, stored);
      } catch {
        // A legacy field missing its type attributes (e.g. enum options) cannot take a typed default.
      }
      if (!matches) fail("Default value does not match the native field type");
      // Every id-shaped media object must sit at an image/file position, as for row drafts.
      const leaves = cmsFieldHasMedia(field as CmsFieldMeta)
        ? cmsMediaIdLeaves(field as CmsFieldMeta, value, new CmsMediaProof())
        : [];
      if (leaves.length !== withIds.count) fail("CMS default carries a media id outside an image or file field");
      const owned = new Map<string, string>();
      for (const leaf of leaves) {
        const record = ctx.media.get(leaf.mediaId);
        if (
          !record ||
          (leaf.fieldType === "image" && record.kind !== "image") ||
          canonicalJson(leaf.metadata) !== canonicalJson(record.metadata)
        )
          fail(MEDIA_ID_MESSAGE);
        owned.set(leaf.path, canonicalJson(leaf.metadata));
      }
      next[locale] = stored;
      pending.set(locale, { canon: canonicalJson(stored), owned });
    }
    field.defaultValueByLocale = next;
    state.pending.set(field, pending);
  }
}

/**
 * Applies the typed changes to a deep copy of the table's current top-level
 * fields. Pure: no I/O. Authorization, the digest compare and the write live in
 * the server operation; the result is verified there against the persisted
 * readback.
 */
export function applyCmsSchemaChanges(
  current: readonly CmsFieldMeta[],
  rawChanges: readonly CmsSchemaChange[],
  ctx: CmsSchemaApplyContext,
): { fields: Field[]; notes: CmsSchemaChangeNote[] } {
  const changes = cmsSchemaChangesSchema.parse(rawChanges);
  const fields: Field[] = JSON.parse(JSON.stringify(current));
  const notes: CmsSchemaChangeNote[] = [];
  const state: MediaState = { baseline: new WeakMap(), pending: new WeakMap() };
  const snapshot = (live: Field[], orig: Field[]) =>
    live.forEach((f, i) => {
      state.baseline.set(f, orig[i]);
      if (isContainer(f) && Array.isArray(f.fields) && Array.isArray(orig[i].fields)) snapshot(f.fields, orig[i].fields);
    });
  snapshot(fields, JSON.parse(JSON.stringify(current)));
  for (const change of changes) {
    switch (change.op) {
      case "add_field": {
        const parentPath = change.parentPath ?? [];
        const { siblings } = siblingsOf(fields, parentPath);
        const topLevel = parentPath.length === 0;
        if (parentPath.length + 1 > CMS_SCHEMA_CHANGE_BOUNDS.pathDepth) fail("CMS schema nesting is too deep");
        const stored = cmsFieldStoredIdentifier(change.identifier, topLevel);
        if (!stored) fail("CMS field identifier normalizes to empty");
        let field = nativeNewField(stored);
        if (change.fieldType !== "text") field = retype(field, change.fieldType).field;
        if (change.set) applySet(field, change.set, topLevel, ctx, state);
        const index = Math.min(change.index ?? siblings.length, siblings.length);
        siblings.splice(index, 0, field);
        assertUniqueSiblings(siblings, [...parentPath, stored].join("/"));
        notes.push({ op: change.op, path: [...parentPath, stored], identifier: stored });
        break;
      }
      case "edit_field": {
        const { field, topLevel } = locate(fields, change.path);
        applySet(field, change.set, topLevel, ctx, state);
        notes.push({ op: change.op, path: [...change.path] });
        break;
      }
      case "rename_field": {
        const { siblings, field, topLevel } = locate(fields, change.path);
        const stored = cmsFieldStoredIdentifier(change.newIdentifier, topLevel);
        if (!stored) fail("CMS field identifier normalizes to empty");
        field.identifier = stored;
        assertUniqueSiblings(siblings, change.path.join("/"));
        notes.push({ op: change.op, path: [...change.path], identifier: stored });
        break;
      }
      case "change_field_type": {
        const { siblings, index, field } = locate(fields, change.path);
        if (field.type === change.fieldType) fail("Field already has this type; a type change would only reset its defaults");
        const out = retype(field, change.fieldType);
        siblings[index] = out.field;
        notes.push({ op: change.op, path: [...change.path], discarded: out.discarded.slice(0, 20) });
        break;
      }
      case "delete_field": {
        const { siblings, index } = locate(fields, change.path);
        siblings.splice(index, 1);
        notes.push({ op: change.op, path: [...change.path] });
        break;
      }
      case "move_field": {
        const { siblings, index } = locate(fields, change.path);
        const [moved] = siblings.splice(index, 1);
        siblings.splice(Math.min(change.toIndex, siblings.length), 0, moved);
        notes.push({ op: change.op, path: [...change.path] });
        break;
      }
    }
    if (countFields(fields) > CMS_SCHEMA_CHANGE_BOUNDS.totalFields) fail("CMS schema exceeds the supported field count");
  }
  assertUniqueSiblings(fields, "top level");
  assertDefaultMediaProvenance(fields, state);
  return { fields, notes };
}

/** The persisted `schema.fields` must be exactly this normalized preimage. */
export const cmsSchemaFieldsCanonical = (fields: readonly unknown[]) =>
  canonicalJson(JSON.parse(JSON.stringify(fields)));
